<template>
  <div
    title="Match percentage"
    v-if="typeof distance === 'number'"
    id="DistanceDisplayer"
    :class="getClassName(percentage)"
  >
    {{ percentage }} {{ "%" }}
  </div>
</template>

<script>
// The distance handed to this component is the SQUARED euclidean distance
// between two unit embeddings, which converts back to a cosine similarity
// exactly: distance = 2 - 2 * cos, so cos = 1 - distance / 2.
//
// Cosine cannot be shown as-is. Two completely unrelated french texts still
// sit around 0.78 under these embeddings, so a raw percentage never leaves
// the 50-80 band and a total miss reads like a half-decent result. The scale
// below stretches the part of the range that actually carries meaning, using
// two anchors measured against this corpus:
//
//   COS_NONE  the noise floor. Two arbitrary slides of this corpus sit at a
//             median of 0.779, and deliberately off-topic queries (a risotto
//             recipe, the offside rule, changing a tyre, the price of bitcoin)
//             still find a best match at 0.761-0.812. Anything at or below
//             that is not a match at all -> 0%.
//   COS_GOOD  matches a reader agrees with land at 0.86-0.94 -> GOOD_SCORE.
//
// Above COS_GOOD the remaining points are spread up to a perfect 1.0, so only
// a near-identical text reaches 100%.
//
// The gap between the worst real match (0.849) and the best off-topic one
// (0.812) is what the floor sits in. Raise COS_NONE and marginal matches read
// as nothing; lower it and an unrelated query starts looking like a result.
const COS_NONE = 0.81;
const COS_GOOD = 0.88;
const GOOD_SCORE = 90;

export default {
  name: "DistanceDisplayer",
  props: {
    distance: {
      type: Number,
    },
  },
  data() {
    return {};
  },
  mounted() {},
  methods: {},
  computed: {
    percentage() {
      const cosine = 1 - this.distance / 2;

      // Not a match: at or below what two unrelated texts already score
      if (cosine <= COS_NONE) {
        return 0;
      }

      // Noise floor -> good match, spread over most of the scale
      if (cosine < COS_GOOD) {
        return Math.round(
          ((cosine - COS_NONE) / (COS_GOOD - COS_NONE)) * GOOD_SCORE
        );
      }

      // Good match -> identical text
      return Math.round(
        GOOD_SCORE + ((cosine - COS_GOOD) / (1 - COS_GOOD)) * (100 - GOOD_SCORE)
      );
    },
    getClassName() {
      return (percentage) => {
        if (percentage >= 85) return "veryGood";
        else if (percentage >= 70) return "good";
        else if (percentage >= 50) return "medium";
        else if (percentage >= 25) return "bad";
        else return "veryBad";
      };
    },
  },
};
</script>

<style lang="scss" scoped>
#DistanceDisplayer {
  white-space: nowrap;
  font-weight: bold;
  color: var(--primary);
  margin-left: 10px;

  &.veryGood {
    color: var(--veryGood);
  }
  &.good {
    color: var(--good);
  }
  &.medium {
    color: var(--medium);
  }
  &.bad {
    color: var(--bad);
  }
  &.veryBad {
    color: var(--veryBad);
  }
}
</style>
